// Geometry for the exported pedigree canvas.
//
// Extracted from ExportMenu's `exportImage` (TGCLOUD-1759) so the arithmetic can
// be pinned by a test. It is pure: measured pixel sizes in, positions out, no
// canvas and no DOM.
//
// The exported document is laid out as
//
//     ┌──────────────────────────────────────────┐
//     │ logo                                     │  header
//     │ identifier                               │  (the two STACK)
//     │                                          │
//     │ pedigree SVG              diagnoses      │  body
//     │                           legend         │
//     │                                          │
//     │                               dates      │
//     └──────────────────────────────────────────┘
//
// Any of the four decorations can be absent: the logo only when the group has
// `showLogoOnExport` off, the legend when no diagnoses are shown, and before
// TGCLOUD-1759 the identifier was absent ALWAYS, because ExportMenu looked up an
// element id that nothing in the application carried.
//
// That permanent zero is what hid the bug fixed here. The header height was
// `Math.max(logoH, identifierH)` while the draw calls stack the identifier BELOW
// the logo, so the two disagreed by the identifier's whole height — and the body
// started far enough up to run underneath it. With identifierH pinned at 0 the
// max and the stack give the same answer, so it never showed. Rendering the
// element would have put the identifier roughly 50px down into the top of the
// pedigree drawing.

// Height of the stacked header, and where each part sits inside it.
//
// `padding` separates consecutive parts; a missing part contributes no height
// and no gap, so a logo-only export is unchanged from before.
//
// TGCLOUD-1791 added a third part, the family note, below the identifier. It is
// computed here AND drawn by ExportMenu from these same values, deliberately in
// one change: the bug described above was exactly a header height and a draw
// order that disagreed, and adding a part to only one of them reproduces it.
export const computeExportHeader = ({
  padding,
  logoHeight = 0,
  identifierHeight = 0,
  familyNoteHeight = 0
}) => {
  const logoH = logoHeight > 0 ? logoHeight : 0;
  const identifierH = identifierHeight > 0 ? identifierHeight : 0;
  const noteH = familyNoteHeight > 0 ? familyNoteHeight : 0;

  // Sum the parts that are present, with one padding BETWEEN each pair. Written
  // as a fold rather than as nested conditionals because three parts give eight
  // presence combinations, and the `Math.max` shortcut that read correctly for
  // two of them is what hid the original defect.
  const parts = [logoH, identifierH, noteH].filter((h) => h > 0);
  const headerH =
    parts.reduce((a, h) => a + h, 0) + padding * Math.max(0, parts.length - 1);

  // Each part is drawn from the left gutter; only the vertical position varies.
  // Walk the same list so a position can never be derived from a different
  // presence rule than the height was.
  let y = padding;
  const logoY = y;
  if (logoH > 0) y += logoH + padding;
  const identifierY = y;
  if (identifierH > 0) y += identifierH + padding;
  const familyNoteY = y;

  return {
    headerH,
    logoY,
    identifierY,
    familyNoteY,
    // Where the body (SVG + legend) starts. No header means no header gap.
    contentY: padding + (headerH > 0 ? headerH + padding : 0)
  };
};

// Overall canvas size.
//
// The width takes the header parts into account as well as the diagram: an
// identifier is a single unwrapped line, so a long primary external ID over a
// narrow pedigree — a one-person family is the realistic case — would otherwise
// be clipped at the canvas edge. The family note is the same problem with a
// wider element, which is why the element itself carries a maxWidth: the note is
// a paragraph, and without a cap a long one would stretch the whole document to
// its length rather than wrapping.
export const computeExportCanvasSize = ({
  padding,
  svgWidth,
  svgHeight,
  identifierWidth = 0,
  familyNoteWidth = 0,
  legendWidth = 0,
  legendHeight = 0,
  datesHeight = 0,
  contentY
}) => {
  const legendGutter = legendWidth > 0 ? padding + legendWidth : 0;
  const bodyH = Math.max(svgHeight, legendHeight);

  return {
    width:
      padding +
      Math.max(svgWidth, identifierWidth, familyNoteWidth) +
      legendGutter +
      padding,
    height:
      contentY +
      bodyH +
      (datesHeight > 0 ? padding + datesHeight : 0) +
      padding,
    bodyH,
    legendGutter
  };
};
